import type { Actor, Kind, Node, Space, StatusDef, View } from "@melo/shared";
import { compareRank, nodeKey } from "@melo/shared/client";
import type { Tables } from "./store";

export const byRank = compareRank;

export function spaceBySlug(t: Tables, slug: string): Space | undefined {
  for (const s of t.spaces.values()) if (s.slug === slug) return s;
  return undefined;
}

export function spacesSorted(t: Tables): Space[] {
  return [...t.spaces.values()].filter((s) => !s.archivedAt).sort(byRank);
}

export function viewsOf(t: Tables, spaceId: string): View[] {
  return [...t.views.values()].filter((v) => v.spaceId === spaceId).sort(byRank);
}

export function kindsOf(t: Tables, spaceId: string): Kind[] {
  const order = ["task", "concept", "system", "phase", "doc"];
  return [...t.kinds.values()]
    .filter((k) => k.spaceId === spaceId)
    .sort((a, b) => (order.indexOf(a.key) + 1 || 99) - (order.indexOf(b.key) + 1 || 99) || a.name.localeCompare(b.name));
}

export function kindByKey(t: Tables, spaceId: string, key: string): Kind | undefined {
  for (const k of t.kinds.values()) if (k.spaceId === spaceId && k.key === key) return k;
  return undefined;
}

/** Working items in a space: not drafts, sorted by rank. */
export function nodesOf(t: Tables, spaceId: string): Node[] {
  return [...t.nodes.values()].filter((n) => n.spaceId === spaceId && !n.draft).sort(byRank);
}

export function childrenOf(t: Tables, nodeId: string): Node[] {
  return [...t.nodes.values()].filter((n) => n.parentId === nodeId && !n.draft).sort(byRank);
}

/** Every node under `rootId`, at any depth. */
export function descendantIds(t: Tables, rootId: string): Set<string> {
  const kids = new Map<string, string[]>();
  for (const n of t.nodes.values()) if (n.parentId) (kids.get(n.parentId) ?? kids.set(n.parentId, []).get(n.parentId)!).push(n.id);
  const out = new Set<string>();
  const stack = [...(kids.get(rootId) ?? [])];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    stack.push(...(kids.get(id) ?? []));
  }
  return out;
}

export function keyOf(t: Tables, n: Pick<Node, "spaceId" | "number">): string {
  return nodeKey(t.spaces.get(n.spaceId)?.prefix ?? "?", n.number);
}

export function findByKey(t: Tables, key: string): Node | undefined {
  const m = /^([A-Z0-9]+)-(\d+)$/i.exec(key);
  if (!m) return t.nodes.get(key);
  const space = [...t.spaces.values()].find((s) => s.prefix === m[1]!.toUpperCase());
  if (!space) return undefined;
  const num = Number(m[2]);
  for (const n of t.nodes.values()) if (n.spaceId === space.id && n.number === num) return n;
  return undefined;
}

export function statusOf(kind: Kind | undefined, key: string): StatusDef | undefined {
  return kind?.statuses.find((s) => s.key === key);
}

export function isDone(t: Tables, n: Node): boolean {
  return statusOf(t.kinds.get(n.kindId), n.status)?.category === "done";
}

export function people(t: Tables): Actor[] {
  return [...t.actors.values()].filter((a) => a.type === "person" && !a.disabled && a.profile !== "system").sort((a, b) => a.name.localeCompare(b.name));
}

export function agents(t: Tables): Actor[] {
  return [...t.actors.values()].filter((a) => a.type === "agent" && !a.disabled).sort((a, b) => a.name.localeCompare(b.name));
}

export function initials(a: Pick<Actor, "name" | "handle">): string {
  const words = (a.name || a.handle).trim().split(/\s+/);
  return (words.length > 1 ? words[0]![0]! + words[1]![0]! : (words[0] ?? "?").slice(0, 2)).toUpperCase();
}

/** Short relative time like "4m", "2h", "3d". */
export function ago(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 45) return "now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)}d`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "just now", "5m ago", "3d ago": for sentences, where a bare "now" would read "sent now ago". */
export function since(iso: string, now = Date.now()): string {
  const a = ago(iso, now);
  return a === "now" ? "just now" : /^\d/.test(a) ? `${a} ago` : `on ${a}`;
}

export function formatDate(d: string | null): string {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  const date = new Date(y!, m! - 1, day!);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
